import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Link,
  Select,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { RiDownloadLine, RiUploadLine } from "react-icons/ri";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import { UploadFileItem } from "src/components/UploadButtons/UploadFileQuestion/UploadFileItem";
import * as FSQ from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import type { GetBoundariesResponse } from "src/generated/hooks/form-service/requests/types.gen";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  generateFeatureNames,
  processFeatureForEdit,
} from "./utils/boundaryUtils";
import { handleGeoJSONFileUpload } from "./utils/fileUploadHandler";
import type { GeoJSONType } from "./utils/types";
import { GeoJSONFeature } from "./utils/types";

type Boundary = GetBoundariesResponse["data"]["boundaries"][0];

interface EditBoundaryDrawerProps {
  boundary: Boundary | null;
  onBoundaryUpdated: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export const EditBoundaryDrawer = ({
  boundary,
  onBoundaryUpdated,
  isOpen,
  onClose,
}: EditBoundaryDrawerProps) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<
    "uploading" | "success" | "error" | null
  >(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [geoJsonType, setGeoJsonType] = useState<GeoJSONType | null>(null);
  const [features, setFeatures] = useState<GeoJSONFeature[]>([]);
  const [availablePropertyKeys, setAvailablePropertyKeys] = useState<string[]>(
    []
  );
  const [selectedNameKey, setSelectedNameKey] = useState<string>("");
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState<number>(0);
  const [boundaryKey, setBoundaryKey] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useAvelaToast();

  const updateBoundaryMutation = FSQ.useUpdateBoundaryById();

  useEffect(() => {
    if (boundary) {
      setBoundaryKey(boundary.key || "");
    }
  }, [boundary]);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    setUploadStatus("uploading");
    setUploadError(null);

    const result = await handleGeoJSONFileUpload(file);

    if (result.isValid && result.geoJsonData && result.geoJsonType) {
      setGeoJsonType(result.geoJsonType);
      setUploadError(null);
      setFeatures(result.features);
      setAvailablePropertyKeys(result.propertyKeys);

      if (result.propertyKeys.length > 0) {
        setSelectedNameKey(result.propertyKeys[0] || "");
      }

      setSelectedFeatureIndex(0);
      setUploadStatus("success");
    } else {
      setUploadStatus("error");
      setUploadError(result.error || "Invalid GeoJSON file");
      if (
        result.error === "Invalid file type. Please upload a .geoJSON file."
      ) {
        toast({
          title: "Invalid file type",
          description: "Please upload a .geoJSON file",
          status: "error",
          duration: 5000,
        });
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadStatus(null);
    setUploadError(null);
    setGeoJsonType(null);
    setFeatures([]);
    setAvailablePropertyKeys([]);
    setSelectedNameKey("");
    setSelectedFeatureIndex(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleExportGeoJSON = async () => {
    if (!boundary) return;

    try {
      const response = await FSR.exportBoundaryGeoJson({
        path: {
          id: boundary.id,
        },
      });

      if (response.data?.data) {
        // Create a download link for the exported geoJSON data
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `${boundary.key || `boundary-${boundary.id}`}.geojson`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error exporting boundary:", error);
      toast({
        title: "Error",
        description: "Failed to export boundary",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleCancel = () => {
    setUploadedFile(null);
    setUploadStatus(null);
    setUploadError(null);
    setGeoJsonType(null);
    setFeatures([]);
    setAvailablePropertyKeys([]);
    setSelectedNameKey("");
    setSelectedFeatureIndex(0);
    setBoundaryKey(boundary?.key || "");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleUpdate = async () => {
    if (!boundary) return;

    const requestBody: {
      key?: string;
      feature?: {
        type: "Feature";
        geometry: {
          type: "Polygon" | "MultiPolygon";
          coordinates?: unknown;
        };
        properties: {
          [key: string]: unknown;
        } | null;
      };
    } = {
      key: boundaryKey,
    };

    if (features.length > 0) {
      // User uploaded new geometry - use the selected feature
      const selectedFeature = features[selectedFeatureIndex];
      if (!selectedFeature) return;

      let featureName: string;
      if (geoJsonType === "FeatureCollection" && selectedNameKey) {
        const result = processFeatureForEdit(
          features,
          selectedFeatureIndex,
          selectedNameKey
        );
        featureName = result.featureName;
      } else if (geoJsonType === "Feature") {
        featureName = boundaryKey;
      } else if (geoJsonType === "Geometry") {
        featureName = boundaryKey;
      } else {
        featureName = `Feature ${selectedFeatureIndex + 1}`;
      }

      requestBody.feature = {
        type: "Feature" as const,
        properties: {
          ...selectedFeature.properties,
          name: featureName,
        },
        geometry: {
          type: selectedFeature.geometry.type as "Polygon" | "MultiPolygon",
          coordinates: selectedFeature.geometry.coordinates,
        },
      };
    }
    // If no new geometry uploaded, only send the key (name) update

    try {
      await updateBoundaryMutation.mutateAsync({
        path: {
          id: boundary.id,
        },
        body: requestBody,
      });

      toast({
        title: "Success",
        description: "Boundary updated successfully",
        status: "success",
        duration: 3000,
      });

      onBoundaryUpdated();
      handleCancel();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update boundary",
        status: "error",
        duration: 5000,
      });
    }
  };

  return (
    <ResizableDrawer
      isOpen={isOpen}
      onClose={handleCancel}
      header={`Edit boundary${
        boundary ? ` - ${boundary.key || boundary.id}` : ""
      }`}
      contentProps={{ bg: "white" }}
      footer={
        <HStack spacing={2} w="full">
          <Button
            variant="outline"
            flex={1}
            size="sm"
            h={8}
            onClick={handleCancel}
            colorScheme="gray"
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            flex={1}
            size="sm"
            h={8}
            onClick={handleUpdate}
            isDisabled={
              !boundaryKey.trim() ||
              !!uploadError ||
              (features.length > 0 &&
                geoJsonType === "FeatureCollection" &&
                !selectedNameKey)
            }
            isLoading={updateBoundaryMutation.isPending}
          >
            Update boundary
          </Button>
        </HStack>
      }
    >
      <VStack spacing={6} align="stretch">
        <FormControl>
          <FormLabel
            fontSize="sm"
            fontWeight="medium"
            color="gray.700"
            mb={1.5}
          >
            Boundary name
          </FormLabel>
          <Input
            value={boundaryKey}
            onChange={(e) => setBoundaryKey(e.target.value)}
            placeholder="Enter boundary name"
            size="md"
            bg="white"
          />
          <Text fontSize="xs" color="gray.500" mt={1.5}>
            Name used to identify this boundary.
          </Text>
        </FormControl>

        <FormControl>
          <FormLabel
            fontSize="sm"
            fontWeight="medium"
            color="gray.700"
            mb={1.5}
          >
            Shape
          </FormLabel>
          <VStack spacing={2} align="stretch">
            {boundary && (
              <HStack justify="space-between" align="center">
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" fontWeight="medium" color="purple.500">
                    {boundary.key || `boundary-${boundary.id}`}.geoJSON
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {boundary.geom
                      ? `${Math.round(
                          JSON.stringify(boundary.geom).length / 1024
                        )}KB`
                      : "0KB"}
                  </Text>
                </VStack>
                <Tooltip label="Export .geoJSON" hasArrow>
                  <IconButton
                    aria-label="Export geoJSON"
                    icon={<RiDownloadLine />}
                    size="lg"
                    variant="ghost"
                    color="gray.500"
                    onClick={handleExportGeoJSON}
                    _hover={{ color: "gray.700" }}
                  />
                </Tooltip>
              </HStack>
            )}
            <Input
              ref={fileInputRef}
              type="file"
              accept=".geojson"
              onChange={handleFileUpload}
              display="none"
            />
            {uploadedFile && uploadStatus && (
              <UploadFileItem
                file={uploadedFile}
                progress={uploadStatus === "uploading" ? 50 : 100}
                status={uploadStatus}
                errorMessage={uploadError || undefined}
                onRemoveFile={handleRemoveFile}
              />
            )}
            <Button
              variant="outline"
              leftIcon={<RiUploadLine />}
              size="md"
              h={10}
              px={4}
              py={2.5}
              borderColor="rgba(0,0,0,0.16)"
              opacity={uploadedFile && uploadStatus === "success" ? 0.5 : 1}
              alignSelf="flex-start"
              onClick={handleUploadClick}
              disabled={uploadedFile !== null && uploadStatus === "success"}
            >
              Upload file
            </Button>
            <Text fontSize="xs" lineHeight="4">
              Optionally upload a GeoJSON file with features to replace the
              current boundary geometry.{" "}
              <Link
                href="https://help.avela.org/hc/en-us/articles/40075093241101-Boundaries"
                color="purple.500"
                textDecoration="underline"
                isExternal
              >
                Learn more about boundaries
              </Link>
            </Text>
          </VStack>
        </FormControl>

        {features.length > 0 &&
          uploadStatus === "success" &&
          geoJsonType === "FeatureCollection" && (
            <>
              <FormControl>
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.700"
                  mb={1.5}
                >
                  Select an identifying property:
                </FormLabel>
                <Select
                  value={selectedNameKey}
                  onChange={(e) => setSelectedNameKey(e.target.value)}
                  placeholder="Select property for feature names"
                  size="md"
                  bg="white"
                >
                  {availablePropertyKeys.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1.5}>
                  Select a property from the file to be used as a boundary name.
                </Text>
              </FormControl>

              {features.length > 1 && (
                <FormControl>
                  <FormLabel
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.700"
                    mb={1.5}
                  >
                    Select feature to replace boundary:
                  </FormLabel>
                  <Select
                    value={selectedFeatureIndex}
                    onChange={(e) =>
                      setSelectedFeatureIndex(parseInt(e.target.value))
                    }
                    size="md"
                    bg="white"
                  >
                    {generateFeatureNames(
                      features,
                      geoJsonType || "FeatureCollection",
                      selectedNameKey
                    ).map((name, index) => (
                      <option key={index} value={index}>
                        {name}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="xs" color="gray.500" mt={1.5}>
                    Choose which feature from the uploaded file will replace the
                    current boundary.
                  </Text>
                </FormControl>
              )}

              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color="gray.700"
                  mb={3}
                  fontFamily="Inter"
                >
                  FEATURES: {features.length}
                </Text>
                <Box
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  bg="gray.50"
                >
                  <Box
                    px={4}
                    py={3}
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    bg="white"
                  >
                    <Text fontSize="sm" color="gray.700" fontWeight="semibold">
                      {selectedNameKey || "Property"}
                    </Text>
                  </Box>
                  {generateFeatureNames(
                    features,
                    geoJsonType || "FeatureCollection",
                    selectedNameKey
                  ).map((name, index) => (
                    <Box
                      key={index}
                      px={4}
                      py={3}
                      borderBottom="1px solid"
                      borderColor="gray.200"
                      _last={{ borderBottom: "none" }}
                      bg={index === selectedFeatureIndex ? "blue.50" : "white"}
                    >
                      <Text fontSize="sm" color="gray.700" fontWeight="medium">
                        {name}{" "}
                        {index === selectedFeatureIndex &&
                          features.length > 1 &&
                          "(Selected)"}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
      </VStack>
    </ResizableDrawer>
  );
};
